import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SettingsSection = ({ currentUser }) => {
  const [tgSettings, setTgSettings] = useState({ enabled: false, bot_token: '', default_chat_id: '', base_url: '', masked: true });
  const [tgForm, setTgForm] = useState({ enabled: false, bot_token: '', default_chat_id: '', base_url: '' });
  const [mailSettings, setMailSettings] = useState({ enabled: false, from_addr: '', reply_to: '', mail_host: '', myhostname: '', mail_server_ip: '', smtp_host: '127.0.0.1', smtp_port: 25, smtp_submission_port: 587, imap_host: '127.0.0.1', imap_port: 993, max_message_size_mb: 25, webmail_url: '', domains: [], dns_recommendations: [] });
  const [mailForm, setMailForm] = useState({ enabled: false, from_addr: '', reply_to: '', myhostname: '', mail_server_ip: '', smtp_host: '127.0.0.1', smtp_port: 25, smtp_submission_port: 587, imap_host: '127.0.0.1', imap_port: 993, max_message_size_mb: 25, webmail_url: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testing, setTesting] = useState(false);
  const [mailTesting, setMailTesting] = useState(false);
  const [settingsSubSection, setSettingsSubSection] = useState('telegram');
  const [dkimLoading, setDkimLoading] = useState(null);
  const [dkimResult, setDkimResult] = useState(null);
  const [mailHelpOpen, setMailHelpOpen] = useState(false);
  const [mailHelpContent, setMailHelpContent] = useState('');

  const token = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [tgRes, mailRes] = await Promise.all([
          axios.get('/api/settings/telegram', { headers: headers() }),
          axios.get('/api/settings/mail', { headers: headers() }).catch(() => ({ data: {} }))
        ]);
        setTgSettings(tgRes.data);
        setTgForm({ enabled: tgRes.data.enabled || false, bot_token: '', default_chat_id: tgRes.data.default_chat_id || '', base_url: tgRes.data.base_url || '' });
        const md = mailRes.data;
        setMailSettings(md);
        setMailForm({ enabled: md.enabled || false, from_addr: md.from_addr || 'noreply@visor.local', reply_to: md.reply_to || '', myhostname: md.myhostname || '', mail_server_ip: md.mail_server_ip || '', smtp_host: md.smtp_host || '127.0.0.1', smtp_port: md.smtp_port ?? 25, smtp_submission_port: md.smtp_submission_port ?? 587, imap_host: md.imap_host || '127.0.0.1', imap_port: md.imap_port ?? 993, max_message_size_mb: md.max_message_size_mb ?? 25, webmail_url: md.webmail_url || '' });
      } catch (err) {
        if (err.response?.status === 403) setError('Доступ только для администратора');
        setTgSettings({ enabled: false, bot_token: '', default_chat_id: '', masked: true });
      }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  useEffect(() => { const t = setTimeout(() => { setSuccess(''); setError(''); }, 5000); return () => clearTimeout(t); }, [success, error]);

  const saveTelegram = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const payload = { enabled: tgForm.enabled, default_chat_id: tgForm.default_chat_id, base_url: (tgForm.base_url || '').trim().replace(/\/+$/, '') };
      if (tgForm.bot_token) payload.bot_token = tgForm.bot_token;
      await axios.put('/api/settings/telegram', payload, { headers: { ...headers(), 'Content-Type': 'application/json' } });
      setSuccess('Настройки Telegram сохранены');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const testTelegram = async () => {
    setError('');
    setSuccess('');
    setTesting(true);
    try {
      await axios.post('/api/settings/telegram/test', {}, { headers: headers() });
      setSuccess('Тестовое сообщение отправлено в Telegram. Проверьте чат.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка отправки теста');
    } finally {
      setTesting(false);
    }
  };

  const saveMail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.put('/api/settings/mail', mailForm, { headers: { ...headers(), 'Content-Type': 'application/json' } });
      setSuccess('Настройки почты сохранены');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const testMail = async () => {
    setError('');
    setSuccess('');
    setMailTesting(true);
    try {
      await axios.post('/api/settings/mail/test', {}, { headers: headers() });
      setSuccess('Тестовое письмо отправлено на ваш email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка отправки теста');
    } finally {
      setMailTesting(false);
    }
  };

  const genDkim = async (domain) => {
    setError('');
    setDkimResult(null);
    setDkimLoading(domain);
    try {
      const { data } = await axios.post('/api/settings/mail/gen-dkim', { domain }, { headers: { ...headers(), 'Content-Type': 'application/json' } });
      setDkimResult({ domain, ...data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка генерации DKIM');
    } finally {
      setDkimLoading(null);
    }
  };

  const openMailHelp = async () => {
    setMailHelpOpen(true);
    if (!mailHelpContent) {
      try {
        const { data } = await axios.get('/api/settings/mail/help', { headers: headers() });
        setMailHelpContent(data.content || '');
      } catch {
        setMailHelpContent('Не удалось загрузить инструкцию.');
      }
    }
  };

  if (loading) return <div className="loading">Загрузка настроек...</div>;

  return (
    <div className="settings-section">
      <div className="content-header">
        <div>
          <h1>Системные настройки</h1>
          <p>Настройка параметров системы и интеграций</p>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div style={{ marginBottom: 24, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={settingsSubSection === 'telegram' ? 'btn-success btn-small' : 'btn-secondary btn-small'}
          onClick={() => setSettingsSubSection('telegram')}
        >
          Telegram
        </button>
        <button
          type="button"
          className={settingsSubSection === 'mail' ? 'btn-success btn-small' : 'btn-secondary btn-small'}
          onClick={() => setSettingsSubSection('mail')}
        >
          Почтовый сервер
        </button>
      </div>

      <div className="user-management" style={{ maxWidth: 600 }}>
        {settingsSubSection === 'telegram' && (
          <>
            <h3 style={{ marginBottom: 16 }}>Telegram</h3>
            <p style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 14 }}>
              Уведомления по проектам (задачи, ВКС, дедлайны). Токен бота — @BotFather, Chat-id — @userinfobot. Базовый URL нужен для ссылок «Открыть в приложении» в уведомлениях.
            </p>
            <form onSubmit={saveTelegram}>
              <div className="form-group">
                <label>
                  <input type="checkbox" checked={tgForm.enabled} onChange={e => setTgForm({ ...tgForm, enabled: e.target.checked })} />
                  Включить уведомления Telegram
                </label>
              </div>
              <div className="form-group">
                <label>Базовый URL приложения</label>
                <input type="url" value={tgForm.base_url} onChange={e => setTgForm({ ...tgForm, base_url: e.target.value })} placeholder="https://192.168.1.100 или https://visor.company.ru" title="Без слэша в конце. Используется для ссылок «Открыть в приложении» в уведомлениях" />
                <small style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Без слэша в конце. Используется для ссылок «Открыть в приложении» в уведомлениях.</small>
              </div>
              <div className="form-group">
                <label>Токен бота</label>
                <input type="password" value={tgForm.bot_token} onChange={e => setTgForm({ ...tgForm, bot_token: e.target.value })} placeholder={tgSettings.masked && tgSettings.bot_token ? '••••••••' : 'Введите токен'} title="Токен от @BotFather в Telegram" />
              </div>
              <div className="form-group">
                <label>Chat-id по умолчанию</label>
                <input type="text" value={tgForm.default_chat_id} onChange={e => setTgForm({ ...tgForm, default_chat_id: e.target.value })} placeholder="Числовой ID чата" title="Числовой ID чата для уведомлений. Узнать: @userinfobot" />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="submit" className="btn-success btn-small">Сохранить</button>
                <button type="button" className="btn-secondary btn-small" onClick={testTelegram} disabled={testing}>Тест</button>
                {testing && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Отправка...</span>}
              </div>
              <p style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>Проверка просроченных задач выполняется автоматически каждый день в 9:00 встроенным планировщиком приложения.</p>
            </form>
          </>
        )}

        {settingsSubSection === 'mail' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Почтовый сервер</h3>
              <button type="button" className="btn-secondary btn-small" onClick={openMailHelp} title="Подробная инструкция по ручной настройке (DNS, службы, Roundcube)">Помощь</button>
            </div>
        <p style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 14 }}>
          Настройка почты: уведомления по email, параметры сервера (Postfix/Dovecot) и дополнительные опции. Домены почты задаются в Организациях (поле «Почтовый домен»).
        </p>

        <div style={{ marginBottom: 24, padding: 16, background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <h4 style={{ marginBottom: 8, fontSize: 14 }}>Уведомления</h4>
          <form onSubmit={saveMail}>
            <div className="form-group">
              <label>
                <input type="checkbox" checked={mailForm.enabled} onChange={e => setMailForm({ ...mailForm, enabled: e.target.checked })} />
                Включить уведомления по email (дублирование Telegram)
              </label>
            </div>
            <div className="form-group">
              <label>Адрес отправителя (From)</label>
              <input type="text" value={mailForm.from_addr} onChange={e => setMailForm({ ...mailForm, from_addr: e.target.value })} placeholder="noreply@example.com" title="Email отправителя уведомлений" />
            </div>
            <div className="form-group">
              <label>Reply-To (опционально)</label>
              <input type="text" value={mailForm.reply_to} onChange={e => setMailForm({ ...mailForm, reply_to: e.target.value })} placeholder="support@example.com" title="Адрес для ответа на письма" />
            </div>
          </form>
        </div>

        <div style={{ marginBottom: 24, padding: 16, background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <h4 style={{ marginBottom: 8, fontSize: 14 }}>Параметры сервера (Postfix / Dovecot)</h4>
          <p style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>Используются для подсказок при настройке почтовых клиентов и Roundcube.</p>
          <form onSubmit={saveMail}>
            <div className="form-group">
              <label>Имя хоста (myhostname)</label>
              <input type="text" value={mailForm.myhostname} onChange={e => setMailForm({ ...mailForm, myhostname: e.target.value })} placeholder="mail.example.com" title="Должно совпадать с A-записью для MX" />
              <small style={{ color: 'var(--text-secondary)', fontSize: 11 }}>Должно совпадать с A-записью для MX</small>
            </div>
            <div className="form-group">
              <label>IP сервера для SPF (опционально)</label>
              <input type="text" value={mailForm.mail_server_ip} onChange={e => setMailForm({ ...mailForm, mail_server_ip: e.target.value })} placeholder="Авто по myhostname" title="Используется в подсказке SPF для DNS. Если пусто — определяется по myhostname" />
              <small style={{ color: 'var(--text-secondary)', fontSize: 11 }}>Для подсказки SPF ниже. Если пусто — по myhostname</small>
            </div>
            <div className="responsive-grid-2">
              <div className="form-group">
                <label>SMTP хост / порт</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" value={mailForm.smtp_host} onChange={e => setMailForm({ ...mailForm, smtp_host: e.target.value })} placeholder="127.0.0.1" style={{ flex: 2 }} title="Хост SMTP-сервера (обычно localhost или 127.0.0.1)" />
                  <input type="number" value={mailForm.smtp_port} onChange={e => setMailForm({ ...mailForm, smtp_port: parseInt(e.target.value, 10) || 25 })} min={1} max={65535} style={{ width: 80 }} title="Порт SMTP (25)" />
                </div>
              </div>
              <div className="form-group">
                <label>Submission порт</label>
                <input type="number" value={mailForm.smtp_submission_port} onChange={e => setMailForm({ ...mailForm, smtp_submission_port: parseInt(e.target.value, 10) || 587 })} min={1} max={65535} style={{ width: 100 }} title="Порт отправки (587)" />
              </div>
            </div>
            <div className="responsive-grid-2">
              <div className="form-group">
                <label>IMAP хост / порт</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" value={mailForm.imap_host} onChange={e => setMailForm({ ...mailForm, imap_host: e.target.value })} placeholder="127.0.0.1" style={{ flex: 2 }} title="Хост IMAP-сервера" />
                  <input type="number" value={mailForm.imap_port} onChange={e => setMailForm({ ...mailForm, imap_port: parseInt(e.target.value, 10) || 993 })} min={1} max={65535} style={{ width: 80 }} title="Порт IMAPS (993)" />
                </div>
              </div>
            </div>
          </form>
        </div>

        <div style={{ marginBottom: 24, padding: 16, background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <h4 style={{ marginBottom: 8, fontSize: 14 }}>Дополнительно</h4>
          <form onSubmit={saveMail}>
            <div className="form-group">
              <label>Макс. размер письма (МБ)</label>
              <input type="number" value={mailForm.max_message_size_mb} onChange={e => setMailForm({ ...mailForm, max_message_size_mb: parseInt(e.target.value, 10) || 25 })} min={1} max={1000} style={{ width: 80 }} title="Применяется к Postfix при сохранении настроек" />
              <small style={{ color: 'var(--text-secondary)', fontSize: 11 }}>Применяется к Postfix при сохранении настроек.</small>
            </div>
            <div className="form-group">
              <label>URL веб-почты (Roundcube)</label>
              <input type="text" value={mailForm.webmail_url} onChange={e => setMailForm({ ...mailForm, webmail_url: e.target.value })} placeholder="https://your-server/mail/" title="Ссылка на веб-интерфейс почты для пользователей" />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
              <button type="submit" className="btn-success btn-small">Сохранить настройки почты</button>
              <button type="button" className="btn-secondary btn-small" onClick={testMail} disabled={mailTesting}>Тест email</button>
            </div>
          </form>
        </div>

        {mailSettings.domains && mailSettings.domains.length > 0 && (
          <div style={{ marginBottom: 24, padding: 16, background: 'var(--bg-secondary)', borderRadius: 8 }}>
            <h4 style={{ marginBottom: 8, fontSize: 14 }}>Активные почтовые домены и рекомендации DNS</h4>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>Заданы в Организациях (mail_domain). Добавьте записи у регистратора DNS.</p>
            {(mailSettings.dns_recommendations || []).map((rec, i) => (
              <div key={i} style={{ marginBottom: 16, padding: 12, background: 'var(--bg-primary)', borderRadius: 6, border: '1px solid var(--border-color, #eee)' }}>
                <strong style={{ fontSize: 13 }}>{rec.domain}</strong>
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  <div><span style={{ color: 'var(--text-secondary)' }}>SPF (TXT для {rec.domain}):</span> <code style={{ wordBreak: 'break-all' }}>{rec.spf_txt}</code></div>
                  <div style={{ marginTop: 4 }}><span style={{ color: 'var(--text-secondary)' }}>DMARC (TXT для _dmarc.{rec.domain}):</span> <code style={{ wordBreak: 'break-all' }}>{rec.dmarc_txt}</code></div>
                </div>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button type="button" className="btn-secondary btn-small" onClick={() => genDkim(rec.domain)} disabled={dkimLoading !== null}>{(dkimLoading === rec.domain) ? 'Генерация...' : 'Сгенерировать DKIM'}</button>
                  {dkimResult && dkimResult.domain === rec.domain && (
                    <span style={{ fontSize: 12 }}>TXT: <code style={{ wordBreak: 'break-all' }}>{dkimResult.txt_name}</code> = &quot;{dkimResult.txt_value ? (dkimResult.txt_value.length > 80 ? dkimResult.txt_value.slice(0, 80) + '...' : dkimResult.txt_value) : ''}&quot;</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

      {mailHelpOpen && (
        <div className="modal-overlay" onClick={() => { setMailHelpOpen(false); }}>
          <div className="modal" style={{ maxWidth: 720, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Инструкция по настройке почтового сервера</h3>
              <button type="button" className="btn-secondary btn-small" onClick={() => setMailHelpOpen(false)}>Закрыть</button>
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, fontFamily: 'inherit', margin: 0 }}>{mailHelpContent || 'Загрузка...'}</pre>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SettingsSection;
